import { useRegisterSW } from 'virtual:pwa-register/react'
import { uiStrings } from '@satu/shared'
import { useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { jumlahKotor, lepasPenjagaTab, useJaga } from '../store/jaga'
import { useUi } from '../store/ui'

/**
 * How long a reload may take to start after "Muat ulang" (the new service worker takes over,
 * then the page reloads). Past it, nothing came: the band and the tab guard work as before.
 */
export const TUNGGU_MUAT_MS = 10_000

const teks = uiStrings.pembaruan

/**
 * A new service worker is waiting. When the outbox is empty and no form would lose anything (no
 * form with unsaved changes, no figure a field-by-field screen could not save) the update
 * applies itself (CONTENT.md section 1); otherwise a surface band asks, and "Nanti" keeps the
 * current version until the next launch. Never a toast, never a modal. "Muat ulang" with
 * unsaved changes asks the shell's "Tinggalkan tanpa menyimpan?" first; the reload it then
 * starts is the app's own, so the browser's "leave site?" does not ask a second time. The
 * band's status region stays mounted; its sentence and buttons are in it only while the band
 * asks (uji B20, D7: folded away, "Versi baru SATU siap" read in the page of a current app).
 */
export function UpdatePrompt() {
  const pending = useUi((s) => s.sync.pending)
  const dirty = useUi((s) => s.dirtyForms)
  // Registrations re-render the band; a field-by-field check is asked at render and on update.
  useJaga((s) => s.aktif)
  useJaga((s) => s.periksa)
  const dismissed = useUi((s) => s.update.dismissed)
  const setUpdate = useUi((s) => s.setUpdate)
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW()

  // The reload has started (Muat ulang, or the automatic update): nothing starts it twice.
  const memuat = useRef(false)
  const jamMuat = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pulih = () => {
    if (jamMuat.current !== null) clearTimeout(jamMuat.current)
    jamMuat.current = null
    memuat.current = false
    useJaga.setState({ tabDilepas: false })
  }
  // The band going away (Meja Bersih hides it) with a reload still awaited: no timer left
  // behind, and the tab guard is given back now.
  useEffect(
    () => () => {
      if (jamMuat.current === null) return
      clearTimeout(jamMuat.current)
      jamMuat.current = null
      memuat.current = false
      useJaga.setState({ tabDilepas: false })
    },
    [],
  )
  const muatUlang = () => {
    if (memuat.current) return
    memuat.current = true
    lepasPenjagaTab()
    // A reload unloads the page and this timer with it; one that never comes is given up.
    jamMuat.current = setTimeout(pulih, TUNGGU_MUAT_MS)
    updateServiceWorker(true).catch(pulih)
  }

  const idle = pending === 0 && dirty === 0 && jumlahKotor(useJaga.getState()) === 0

  useEffect(() => {
    setUpdate({ needRefresh })
  }, [needRefresh, setUpdate])

  useEffect(() => {
    if (!needRefresh || !idle || dismissed) return
    // Asked again at the moment of the update: a field-by-field check reads its own state.
    if (jumlahKotor(useJaga.getState()) === 0) muatUlang()
  })

  const show = needRefresh && !idle && !dismissed

  return (
    <div className="band-grow" data-open={show} aria-hidden={!show} inert={!show}>
      <div>
        <div
          role="status"
          className={
            show
              ? 'flex flex-wrap items-center gap-2 border-b border-border bg-surface px-4 py-2 text-label text-text md:px-6 lg:px-8'
              : undefined
          }
        >
          {show ? (
            <>
              <p className="min-w-[16rem] flex-1">{teks.kalimat}</p>
              <Button variant="outline" onClick={() => useJaga.getState().tanya(muatUlang)}>
                {teks.muatUlang}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setUpdate({ dismissed: true })
                  setNeedRefresh(false)
                }}
              >
                {teks.nanti}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
